// kitchen/web/src/lib/history-filter.ts
// Feature 148 — Storico ondate del cuoco (US2).
// Funzione pura che, dato il payload ordini di getOrders, seleziona ciò che va
// mostrato nella vista "Storico" della giornata e lo ordina (più recente in cima).
//
// Regole (spec 148, Q3=B):
//   - Ordine COMPLETED  → incluso con TUTTI i suoi item (terminali).
//   - Ordine CANCELLED  → incluso ma marcato distinto (FR-011), con tutti gli item.
//   - Ordine ancora attivo con ≥1 item SERVED → incluso, ma con i SOLI item serviti
//     (l'ondata servita di un tavolo ancora aperto), NON i piatti pendenti.
//   - Ordine attivo senza item serviti → escluso.
//
// Nessuna dipendenza da React o dalla rete: testabile in isolamento.
//
// Feature 298 — "Tavolo N" and the fallback dish name come from the caller's
// translator `t`; without one they are the Italian source.

import { sourceT, type SharedT } from './source-text';

export type HistoryOrderStatus = 'completed' | 'cancelled' | 'served';

export interface HistoryItem {
  id: string;
  quantity: number;
  dishName: string;
  notes: string | null;
  modifications: string | null;
  variantName: string | null;
  serviceWave: number | null;
  status: string;
}

export interface HistoryEntry {
  orderId: string;
  dailyNumber: number | null;
  tableLabel: string | null;
  /** Natura dell'elemento nello storico (per label/stile, FR-011). */
  status: HistoryOrderStatus;
  /** Timestamp (ms) usato per l'ordinamento discendente. */
  sortAt: number;
  items: HistoryItem[];
}

// Input volutamente permissivo: il client API di kitchen/web tipizza gli ordini
// come `any`. Estraiamo solo i campi che ci servono in modo difensivo.
type AnyOrder = Record<string, any>;
type AnyItem = Record<string, any>;

function toMs(value: unknown): number {
  if (typeof value !== 'string' || value.length === 0) return 0;
  const t = Date.parse(value);
  return Number.isNaN(t) ? 0 : t;
}

function tableLabelOf(order: AnyOrder, t: SharedT): string | null {
  const table = order.table;
  if (table && typeof table === 'object') {
    if (typeof table.name === 'string' && table.name.trim() !== '') {
      return table.name;
    }
    if (table.tableNumber != null) {
      return t('kds.order.tableNumber', 'Tavolo {{number}}', { number: table.tableNumber });
    }
  }
  return null;
}

function mapItem(item: AnyItem, t: SharedT): HistoryItem {
  const variantName =
    item.variant && typeof item.variant === 'object' ? (item.variant.name ?? null) : null;
  return {
    id: String(item.id),
    quantity: typeof item.quantity === 'number' ? item.quantity : 1,
    dishName: item.dish?.name ?? t('kds.dish.fallbackName', 'Piatto'),
    notes: item.notes ?? null,
    modifications: item.modifications ?? null,
    variantName,
    serviceWave:
      typeof item.serviceWave === 'number'
        ? item.serviceWave
        : typeof item.courseNumber === 'number'
          ? item.courseNumber
          : null,
    status: String(item.status ?? ''),
  };
}

/**
 * Seleziona e ordina gli elementi dello Storico della giornata.
 * @param orders payload ordini (forma getOrders). Tollerante a null/non-array.
 * @param t traduttore del chiamante (feature 298); senza, i testi in italiano.
 */
export function selectHistory(
  orders: AnyOrder[] | null | undefined,
  t: SharedT = sourceT,
): HistoryEntry[] {
  if (!Array.isArray(orders)) return [];

  const entries: HistoryEntry[] = [];
  // Dedupe difensivo: lo Storico unisce più bucket (oggi + ieri UTC) che sono
  // disgiunti per numberDate, ma un ordine non deve mai comparire due volte.
  const seen = new Set<string>();

  for (const order of orders) {
    if (!order || typeof order !== 'object') continue;
    const orderId = String(order.id);
    if (seen.has(orderId)) continue;
    seen.add(orderId);
    const orderStatus = String(order.status ?? '');
    const items: AnyItem[] = Array.isArray(order.items) ? order.items : [];

    let status: HistoryOrderStatus | null = null;
    let displayItems: AnyItem[] = [];
    let sortAt = 0;

    if (orderStatus === 'COMPLETED') {
      status = 'completed';
      displayItems = items;
      sortAt = toMs(order.completedAt) || toMs(order.updatedAt) || toMs(order.createdAt);
    } else if (orderStatus === 'CANCELLED') {
      status = 'cancelled';
      displayItems = items;
      sortAt = toMs(order.cancelledAt) || toMs(order.updatedAt) || toMs(order.createdAt);
    } else {
      // Ordine ancora attivo: includi solo se ha ondate/piatti già serviti (Q3=B).
      const served = items.filter((it) => String(it?.status ?? '') === 'SERVED');
      if (served.length === 0) continue;
      status = 'served';
      displayItems = served;
      sortAt = served.reduce((max, it) => Math.max(max, toMs(it.servedAt)), 0);
      if (sortAt === 0) sortAt = toMs(order.updatedAt) || toMs(order.createdAt);
    }

    entries.push({
      orderId: String(order.id),
      dailyNumber: typeof order.dailyNumber === 'number' ? order.dailyNumber : null,
      tableLabel: tableLabelOf(order, t),
      status,
      sortAt,
      items: displayItems.map((it) => mapItem(it, t)),
    });
  }

  // Più recente in cima; tie-break stabile per dailyNumber desc.
  entries.sort((a, b) => {
    if (b.sortAt !== a.sortAt) return b.sortAt - a.sortAt;
    return (b.dailyNumber ?? 0) - (a.dailyNumber ?? 0);
  });

  return entries;
}

// ─── filtro giornata di servizio (business-day cutoff) ───────────────────────

// Chiave "giorno di servizio" (YYYY-MM-DD) in fuso Europe/Rome per un istante:
// la data di calendario Rome, spostata al giorno precedente se l'ora Rome è
// prima del cutoff (es. le 03:00 con cutoff 4 appartengono ancora al giorno
// prima). DST-safe: usa Intl per estrarre ora/data Rome, l'aritmetica è su un
// bucket UTC solo per etichettare (nessuna conversione wall-time→UTC).
function romeBusinessDateKey(ms: number, cutoffHour: number): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Rome',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(ms));
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  const y = get('year');
  const m = get('month');
  const d = get('day');
  const hour = parseInt(get('hour'), 10);
  const base = new Date(`${y}-${m}-${d}T00:00:00Z`);
  if (cutoffHour > 0 && Number.isFinite(hour) && hour < cutoffHour) {
    base.setUTCDate(base.getUTCDate() - 1);
  }
  return base.toISOString().slice(0, 10);
}

/**
 * Tiene solo gli elementi della **giornata di servizio corrente** secondo il
 * cutoff business-day (es. 04:00 Rome → il giorno rolla alle 4, non a mezzanotte
 * UTC). Necessario perché lo Storico carica 2 bucket UTC (oggi+ieri) per non
 * perdere nulla al confine di mezzanotte UTC, ma poi va limitato al giorno di
 * servizio giusto.
 * @param nowMs istante "adesso" (Date.now()); parametrizzato per testabilità.
 */
export function filterCurrentBusinessDay(
  entries: HistoryEntry[],
  cutoffHour: number,
  nowMs: number,
): HistoryEntry[] {
  if (!Number.isFinite(cutoffHour)) return entries;
  const todayKey = romeBusinessDateKey(nowMs, cutoffHour);
  return entries.filter(
    (e) => e.sortAt > 0 && romeBusinessDateKey(e.sortAt, cutoffHour) === todayKey,
  );
}
